<script setup lang="ts">
import { computed } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { officesApi, type Office, type OfficeForm } from '@/api/offices';
import { communesApi } from '@/api/communes';

interface Props {
  office?: Office | null;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const queryClient = useQueryClient();

const isEditing = !!props.office;
const title = computed(() => (isEditing ? 'Editar Oficina' : 'Nueva Oficina'));

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 100;
const MIN_ADDRESS_LENGTH = 5;
const MAX_ADDRESS_LENGTH = 200;

const validationSchema = yup.object({
  name: yup
    .string()
    .required('El nombre es requerido')
    .min(MIN_NAME_LENGTH, `El nombre debe tener al menos ${MIN_NAME_LENGTH} caracteres`)
    .max(MAX_NAME_LENGTH, `El nombre no puede exceder ${MAX_NAME_LENGTH} caracteres`),
  address: yup
    .string()
    .required('La dirección es requerida')
    .min(MIN_ADDRESS_LENGTH, `La dirección debe tener al menos ${MIN_ADDRESS_LENGTH} caracteres`)
    .max(MAX_ADDRESS_LENGTH, `La dirección no puede exceder ${MAX_ADDRESS_LENGTH} caracteres`),
  communeId: yup
    .number()
    .required('La comuna es requerida'),
});

const { values, errors, defineField, handleSubmit, meta } = useForm<OfficeForm>({
  validationSchema,
  initialValues: {
    name: props.office?.name || '',
    address: props.office?.address || '',
    communeId: props.office?.communeId || undefined,
  },
});

const [name, nameAttrs] = defineField('name');
const [address, addressAttrs] = defineField('address');
const [communeId, communeIdAttrs] = defineField('communeId');

const { data: communes } = useQuery({
  queryKey: ['communes'],
  queryFn: () => communesApi.index(),
});

const createMutation = useMutation({
  mutationFn: () => officesApi.create(values),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['offices'] });
    emit('success');
    emit('close');
  },
});

const updateMutation = useMutation({
  mutationFn: () => officesApi.update({ ...values, id: props.office!.id }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['offices'] });
    emit('success');
    emit('close');
  },
});

const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

const onSubmit = handleSubmit(() => {
  if (isEditing && props.office) {
    updateMutation.mutate();
  } else {
    createMutation.mutate();
  }
});

function handleClose() {
  emit('close');
}
</script>
<template>
  <v-card>
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <form @submit.prevent="onSubmit">
        <v-text-field
          v-model="name"
          label="Nombre de la oficina"
          :error-messages="errors.name"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="nameAttrs"
        />

        <v-text-field
          v-model="address"
          label="Dirección"
          :error-messages="errors.address"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="addressAttrs"
        />

        <v-autocomplete
          v-model="communeId"
          :items="communes"
          item-title="name"
          item-value="id"
          label="Comuna"
          :error-messages="errors.communeId"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="communeIdAttrs"
        />
      </form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="isLoading"
        @click="handleClose"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!meta.valid"
        :loading="isLoading"
        @click="onSubmit"
      >
        {{ isEditing ? 'Actualizar' : 'Crear' }}
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="createMutation.isError.value || updateMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente. Recuerda que el nombre no puede repetirse.
    </v-alert>
  </v-card>
</template>
